import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";

const MisInformes = () => {
    const [informes, setInformes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isLoggedIn, setIsLoggedIn] = useState(false);

    const navigate = useNavigate();

    useEffect(() => {
        const checkUser = async () => {
            try {
                const res = await fetch(`${window.REACT_APP_API_ENDPOINT}/api/users/current-user`, { credentials: "include" });
                const data = await res.json();
                if (data.user) {
                    setIsLoggedIn(true);
                } else {
                    navigate('/');
                }
            } catch (err) {
                console.error("Error checking user:", err);
                navigate('/');
            }
        };

        checkUser();
    }, []);

    useEffect(() => {
        if (!isLoggedIn) return;

        const fetchInformes = async () => {
            try {
                const res = await fetch(`${window.REACT_APP_API_ENDPOINT}/api/v2/mis-informes`, {
                    credentials: "include"
                });
                if (!res.ok) {
                    throw new Error('Error al cargar los informes');
                }
                const data = await res.json();
                setInformes(data.informes || []);
            } catch (err) {
                console.error("Error fetching informes:", err);
                setError("Error al cargar tus informes");
            } finally {
                setLoading(false);
            }
        };

        fetchInformes();
    }, [isLoggedIn]);

    const formatFecha = (dateStr) => {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    const formatPrecio = (precio) => {
        if (precio == null) return null;
        return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(precio);
    };

    if (loading) {
        return <div className="container mx-auto p-6">Cargando...</div>;
    }

    if (error) {
        return (
            <div className="container mx-auto p-6">
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                    {error}
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto p-6">
            <h1 className="text-2xl font-bold mb-6">Mis Informes</h1>
            {informes.length === 0 ? (
                <div className="text-center py-12">
                    <p className="text-gray-600 mb-4">Todavía no has generado ningún informe de inversión.</p>
                    <Link
                        to="/listado"
                        className="inline-block bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                    >
                        Explorar propiedades
                    </Link>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {informes.map((informe) => (
                        <div key={informe.propiedad_id} className="bg-white rounded-lg shadow overflow-hidden flex flex-col">
                            <div className="h-48 bg-gray-200 overflow-hidden">
                                {informe.imagen_url ? (
                                    <img
                                        src={informe.imagen_url}
                                        alt={informe.titulo || 'Propiedad'}
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                                        <svg className="h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                                            <polyline strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} points="9 22 9 12 15 12 15 22" />
                                        </svg>
                                    </div>
                                )}
                            </div>
                            <div className="p-4 flex flex-col flex-1">
                                <h2 className="font-semibold text-gray-900 text-sm leading-snug mb-1 line-clamp-2">
                                    {informe.titulo || 'Propiedad sin título'}
                                </h2>
                                {informe.direccion && (
                                    <p className="text-xs text-gray-500 mb-2 line-clamp-1">{informe.direccion}</p>
                                )}
                                {informe.precio != null && (
                                    <p className="text-base font-bold text-blue-700 mb-1">{formatPrecio(informe.precio)}</p>
                                )}
                                <p className="text-xs text-gray-400 mb-4">
                                    Informe generado el {formatFecha(informe.created_at)}
                                </p>
                                <div className="mt-auto">
                                    <Link
                                        to={`/analizarpropiedad/${informe.public_id}/informe-detalle`}
                                        state={{ from: 'mis-informes' }}
                                        className="block w-full text-center bg-blue-600 text-white text-sm px-4 py-2 rounded hover:bg-blue-700"
                                    >
                                        Ver informe
                                    </Link>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default MisInformes;
